'use client';

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { IconName, SymbolComponent } from '../types.js';
import iconNames from '../icon-names.js';

declare global {
  interface Window {
    __iconRegistry: {
      hasSymbol: (name: IconName) => boolean;
      loadSymbol: (name: IconName) => void;
    } | undefined;
  }
}

const CONTAINER_ID = 'svg-container';

export const IconRegistry = ({ loader }: { loader: (name: IconName) => SymbolComponent | Promise<SymbolComponent> }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const symbolsRef = useRef(new Set<IconName>());
  const [loadedSymbols, setLoadedSymbols] = useState<Partial<Record<IconName, SymbolComponent>>>({});

  useEffect(() => {
    let symbolContainer = document.getElementById(CONTAINER_ID);

    if (!symbolContainer) {
      symbolContainer = document.createElement('div');
      symbolContainer.id = CONTAINER_ID;
      symbolContainer.setAttribute('aria-hidden', 'true');
      symbolContainer.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
      document.body.appendChild(symbolContainer);
    }

    setContainer(symbolContainer);

    return () => {
      if (symbolContainer && symbolsRef.current.size === 0) {
        symbolContainer.remove();
      }
    };
  }, []);

  const loadSymbol = useCallback(async (name: IconName) => {
    if (symbolsRef.current.has(name)) return;

    try {
      if (!iconNames.includes(name)) {
        throw new Error(`Icon "${name}" not found in registry`);
      }

      const Symbol = await loader(name);

      if (!Symbol) {
        throw new Error(`Invalid symbol component for "${name}"`);
      }

      setLoadedSymbols(prev => ({
        ...prev,
        [name]: Symbol,
      }));
      symbolsRef.current.add(name);
    } catch (error: any) {
      console.error('Failed to load symbol for icon: ', error);
    }
  }, [loader]);

  const registry = useMemo(() => ({
    loadSymbol,
    hasSymbol: (name: IconName) => symbolsRef.current.has(name),
  }), [loadSymbol]);

  useEffect(() => {
    if (!container) return;

    window.__iconRegistry = registry;
    return () => {
      delete window.__iconRegistry;
    };
  }, [container, registry]);

  const portalContent = useMemo(() => {
    if (!container) {
      return null;
    }

    return (
      <svg>
        {Object.entries(loadedSymbols).map(([name, Symbol]) => (
          <Symbol key={name} id={`svg-container-${name}`} />
        ))}
      </svg>
    );
  }, [loadedSymbols, container]);

  if (!container) {
    return null;
  }

  return createPortal(portalContent, container);
};
